import { ReactElement } from 'react';
import { Doc, PermissionsModel } from '../../types';

import LockIcon from '@mui/icons-material/Lock';
import BusinessIcon from '@mui/icons-material/Business';
import LinkIcon from '@mui/icons-material/Link';
import PublicIcon from '@mui/icons-material/Public';

export interface VisibilityInfo {
  icon: ReactElement;
  color: 'default' | 'info' | 'warning';
  tooltip: string;
  role?: string;
}

export interface VisibilityDetails {
  type: 'restricted' | 'domain' | 'domainWithLink' | 'anyoneWithLink';
  role?: string;
}

export function getRoleActionText(role?: string): string {
  switch (role) {
    case 'owner':
    case 'organizer':
    case 'fileOrganizer':
    case 'writer':
      return 'edit';
    case 'commenter':
      return 'comment';
    case 'reader':
    default:
      return 'view';
  }
}

export function determineVisibilityFromPermissions(
  permissions: PermissionsModel[] | null
): VisibilityDetails {
  if (!permissions || permissions.length === 0) {
    return { type: 'restricted' };
  }
  const anyonePermission = permissions.find((p) => p.type === 'anyone');
  if (anyonePermission) {
    return { type: 'anyoneWithLink', role: anyonePermission.role };
  }

  const domainPermission = permissions.find((p) => p.type === 'domain');
  if (domainPermission) {
    return {
      type: domainPermission.allowFileDiscovery ? 'domain' : 'domainWithLink',
      role: domainPermission.role,
    };
  }

  return { type: 'restricted' };
}

export function getVisibilityInfo(
  doc: Doc,
  primaryDomain: string
): VisibilityInfo {
  const visibilityDetails = determineVisibilityFromPermissions(doc.permissions);

  if (visibilityDetails.type === 'restricted') {
    return {
      icon: <LockIcon sx={{ fontSize: 20 }} color="action" />,
      color: 'default' as const,
      tooltip: 'Only people with access can open with the link',
    };
  }

  const actionText = getRoleActionText(visibilityDetails.role);

  switch (visibilityDetails.type) {
    case 'domain':
      return {
        icon: <BusinessIcon sx={{ fontSize: 20 }} color="info" />,
        color: 'info' as const,
        tooltip: `Anyone at ${primaryDomain} can find and ${actionText}`,
        role: visibilityDetails.role,
      };

    case 'domainWithLink':
      return {
        icon: <LinkIcon sx={{ fontSize: 20 }} color="info" />,
        color: 'info' as const,
        tooltip: `Anyone at ${primaryDomain} with the link can ${actionText}`,
        role: visibilityDetails.role,
      };

    case 'anyoneWithLink':
      return {
        icon: <PublicIcon sx={{ fontSize: 20 }} color="warning" />,
        color: 'warning' as const,
        tooltip: `Anyone on the internet with the link can ${actionText}`,
        role: visibilityDetails.role,
      };

    default:
      return {
        icon: <LockIcon sx={{ fontSize: 20 }} color="action" />,
        color: 'default' as const,
        tooltip: 'Only people with access can open with the link',
      };
  }
}
